type SitePromoBannerRoute =
  | { path: string; match: 'exact' | 'subtree' }
  | { articleKeyword: string };

export type SitePromoBannerCampaign = {
  /** A new ID lets a visitor see a new campaign after dismissing an older one. */
  id: string;
  message: string;
  link: { label: string; href: string };
  /** Short copy for a single-row banner at mobile widths. */
  mobile: { message: string; linkLabel: string };
  /** Omit for the quiet strip. Use brand for blue with white text. */
  variant?: 'quiet' | 'brand';
  /** Omit for an inline link. A button keeps link navigation semantics. */
  linkStyle?: 'text' | 'button';
  /** ISO timestamps with an explicit timezone. Start is inclusive; expiration is exclusive. */
  startsAt: string;
  expiresAt: string;
  include: readonly SitePromoBannerRoute[];
  exclude?: readonly SitePromoBannerRoute[];
};

/** One Journey target across the site's apps and both banner link styles. */
export function sitePromoBannerJourneyTarget(campaignId: string): string {
  return `opendoor-site-promo-banner-${campaignId}`;
}

const SITE_ORIGIN = 'https://www.opendoor.com';
const MOBILE_MESSAGE_MAX_LENGTH = 44;
const MOBILE_LINK_LABEL_MAX_LENGTH = 10;
const MOBILE_TEXT_LINK_COPY_MAX_LENGTH = 28;
const ISO_WITH_TIMEZONE =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,3})?(?:Z|[+-](\d{2}):(\d{2}))$/;

function isSafeLink(href: string): boolean {
  try {
    const url = new URL(href, SITE_ORIGIN);
    if (href.startsWith('/') && !href.startsWith('//') && !href.startsWith('/\\')) {
      return url.origin === SITE_ORIGIN;
    }
    return (
      href.startsWith('https://') && url.protocol === 'https:' && !url.username && !url.password
    );
  } catch {
    return false;
  }
}

function isValidRoute(route: SitePromoBannerRoute): boolean {
  if ('articleKeyword' in route) {
    return /^[a-zA-Z][a-zA-Z0-9]*$/.test(route.articleKeyword);
  }
  if (!route.path.startsWith('/') || route.path.includes('\\') || route.path.includes('?')) {
    return false;
  }
  if (route.path !== '/' && route.path.endsWith('/')) return false;
  try {
    return new URL(route.path, SITE_ORIGIN).pathname === route.path;
  } catch {
    return false;
  }
}

function parseZonedTimestamp(value: string): number | null {
  const parts = ISO_WITH_TIMEZONE.exec(value);
  if (!parts) return null;
  const [
    ,
    yearText,
    monthText,
    dayText,
    hourText,
    minuteText,
    secondText,
    offsetHourText,
    offsetMinuteText,
  ] = parts;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const second = Number(secondText);
  const offsetHour = Number(offsetHourText ?? 0);
  const offsetMinute = Number(offsetMinuteText ?? 0);
  const lastDay = new Date(0);
  lastDay.setUTCFullYear(year, month, 0);
  if (
    year < 1 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > lastDay.getUTCDate() ||
    hour > 23 ||
    minute > 59 ||
    second > 59 ||
    offsetHour > 23 ||
    offsetMinute > 59
  )
    return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function campaignWindow(campaign: SitePromoBannerCampaign): { start: number; end: number } | null {
  const start = parseZonedTimestamp(campaign.startsAt);
  const end = parseZonedTimestamp(campaign.expiresAt);
  if (start === null || end === null) return null;
  return start < end ? { start, end } : null;
}

function isValidCampaign(campaign: SitePromoBannerCampaign): boolean {
  const mobileMessage = campaign.mobile.message;
  const mobileLinkLabel = campaign.mobile.linkLabel;
  return (
    /^[a-z0-9][a-z0-9-]*$/.test(campaign.id) &&
    !!campaign.message.trim() &&
    !!campaign.link.label.trim() &&
    mobileMessage.length > 0 &&
    mobileMessage.trim() === mobileMessage &&
    mobileMessage.length <= MOBILE_MESSAGE_MAX_LENGTH &&
    mobileLinkLabel.length > 0 &&
    mobileLinkLabel.trim() === mobileLinkLabel &&
    mobileLinkLabel.length <= MOBILE_LINK_LABEL_MAX_LENGTH &&
    (campaign.linkStyle === 'button' ||
      mobileMessage.length + mobileLinkLabel.length <= MOBILE_TEXT_LINK_COPY_MAX_LENGTH) &&
    isSafeLink(campaign.link.href) &&
    campaign.include.length > 0 &&
    campaign.include.every(isValidRoute) &&
    (campaign.exclude?.every(isValidRoute) ?? true) &&
    campaignWindow(campaign) !== null
  );
}

function matchesRoute(
  route: SitePromoBannerRoute,
  pathname: string,
  articleKeywords?: readonly string[],
): boolean {
  if ('articleKeyword' in route) {
    return (
      pathname.startsWith('/articles/') &&
      !pathname.startsWith('/articles/topic/') &&
      articleKeywords?.includes(route.articleKeyword) === true
    );
  }
  return (
    pathname === route.path ||
    (route.match === 'subtree' && (route.path === '/' || pathname.startsWith(`${route.path}/`)))
  );
}

type ResolveSitePromoBannerOptions = {
  campaign: SitePromoBannerCampaign | null;
  pathname: string | null;
  nowMs: number;
  articleKeywords?: readonly string[];
};

export function resolveSitePromoBanner({
  campaign,
  pathname,
  nowMs,
  articleKeywords,
}: ResolveSitePromoBannerOptions): SitePromoBannerCampaign | null {
  if (!campaign || !isValidCampaign(campaign) || !Number.isFinite(nowMs)) return null;
  const window = campaignWindow(campaign);
  if (!window || nowMs < window.start || nowMs >= window.end) return null;
  if (pathname === null || !pathname.startsWith('/')) return null;
  const path = pathname.replace(/\/+$/, '') || '/';
  if (!campaign.include.some((route) => matchesRoute(route, path, articleKeywords))) return null;
  if (campaign.exclude?.some((route) => matchesRoute(route, path, articleKeywords))) return null;
  return campaign;
}

export function nextSitePromoBannerBoundary(
  campaign: SitePromoBannerCampaign | null,
  nowMs: number,
): number | null {
  if (!campaign || !isValidCampaign(campaign) || !Number.isFinite(nowMs)) return null;
  const window = campaignWindow(campaign);
  if (!window) return null;
  if (nowMs < window.start) return window.start;
  if (nowMs < window.end) return window.end;
  return null;
}
